import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Label,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap"
import { IoMdEye, IoMdEyeOff } from "react-icons/io"
import { FaSave, FaUserCircle, FaEnvelope, FaPhoneAlt, FaLock } from "react-icons/fa"
import { MdCancel, MdBadge } from "react-icons/md"
import Swal from "sweetalert2"
import secureLocalStorage from "react-secure-storage"
import { FetchPutData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import Configs from "config"
import user1 from "assets/images/users/avatar-9.png"

const emptyPasswords = () => ({
  newPassword: "",
  confirmPassword: "",
})

const ProfileModal = ({ open, close }) => {
  const user = secureLocalStorage?.getItem("admin_user")

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(emptyPasswords())
  const [errors, setErrors] = useState({})
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    if (open) {
      setFormData(emptyPasswords())
      setErrors({})
      setShowNew(false)
      setShowConfirm(false)
    }
  }, [open])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: "" }))
  }

  const validate = () => {
    const errs = {}

    if (!formData.newPassword) {
      errs.newPassword = "New password is required"
    } else if (formData.newPassword.length < 6) {
      errs.newPassword = "Password must be at least 6 characters"
    }

    if (!formData.confirmPassword) {
      errs.confirmPassword = "Please confirm your new password"
    } else if (formData.newPassword !== formData.confirmPassword) {
      errs.confirmPassword = "Passwords do not match"
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!user?.id) return
    if (!validate()) return

    // Same "staff" update endpoint used when editing a staff member —
    // only the password field is sent so name/email/image stay untouched.
    const APIData = await FetchPutData(setLoading, "staff", user.id, {
      password: formData.newPassword,
    })

    if (APIData?.data?.success) {
      Swal.fire({
        title: "Success!",
        text: APIData?.data?.message || "Password changed successfully.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      close()
    } else {
      Swal.fire({
        title: "Failed",
        text:
          APIData?.response?.data?.message ||
          "Could not change password. Please try again.",
        icon: "error",
      })
    }
  }

  const renderPasswordField = (name, label, show, setShow, placeholder) => (
    <div className="mb-3">
      <Label className="form-label fw-semibold">
        {label} <span className="text-danger">*</span>
      </Label>
      <div className="position-relative">
        <Input
          name={name}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={formData[name]}
          onChange={handleChange}
          invalid={!!errors[name]}
          autoComplete="new-password"
          style={{ paddingRight: "40px", borderRadius: "8px" }}
        />
        <span
          onClick={() => setShow(prev => !prev)}
          style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
            color: "#6c757d",
          }}
        >
          {show ? <IoMdEye size={18} /> : <IoMdEyeOff size={18} />}
        </span>
      </div>
      {errors[name] && (
        <span className="text-danger small">{errors[name]}</span>
      )}
    </div>
  )

  return (
    <Modal isOpen={open} toggle={close} centered size="lg">
      {loading && <Loader />}
      <ModalHeader toggle={close}>
        <span className="d-flex align-items-center fw-bold" style={{ color: "#02215c" }}>
          <FaUserCircle className="me-2" size={20} />
          My Profile
        </span>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody style={{ background: "#f7f8fb" }}>
          <Row className="g-4">
            {/* ===== Profile summary card ===== */}
            <Col md={5}>
              <Card
                className="h-100 border-0 shadow-sm text-center"
                style={{ borderRadius: "14px", overflow: "hidden" }}
              >
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #02215c 0%, #0b3a8a 100%)",
                    height: "70px",
                  }}
                />
                <CardBody
                  className="d-flex flex-column align-items-center"
                  style={{ marginTop: "-45px" }}
                >
                  <img
                    src={
                      user?.profilePicture
                        ? `${Configs.imageUrl}${user?.profilePicture}`
                        : user1
                    }
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: 90,
                      height: 90,
                      objectFit: "cover",
                      border: "4px solid #fff",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                    }}
                  />

                  <h4 className="mt-3 mb-0 fw-bold">{user?.name || "--"}</h4>

                  {user?.designation && (
                    <span
                      className="badge mt-2 px-3 py-1"
                      style={{
                        background: "#eef1fb",
                        color: "#02215c",
                        fontWeight: 600,
                        borderRadius: "20px",
                        fontSize: "12px"
                      }}
                    >
                      <MdBadge className="me-1" />
                      {user.designation}
                    </span>
                  )}

                  <hr className="w-100 mt-2 mb-3" />

                  <div className="w-100 text-start">
                    <div className="d-flex align-items-center gap-2 text-muted mb-2 fs-5">
                      <FaEnvelope size={13} />
                      <span className="small text-truncate">
                        {user?.email || "--"}
                      </span>
                    </div>
                    {user?.phone && (
                      <div className="d-flex align-items-center gap-2 text-muted fs-5">
                        <FaPhoneAlt size={13} />
                        <span className="small">{user.phone}</span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* ===== Change password card ===== */}
            <Col md={7}>
              <Card
                className="h-100 border-0 shadow-sm"
                style={{ borderRadius: "14px" }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 32,
                        height: 32,
                        background: "#eef1fb",
                        color: "#02215c",
                      }}
                    >
                      <FaLock size={14} />
                    </span>
                    <h6 className="mb-0 fw-bold">Change Password</h6>
                  </div>
                  <p className="text-muted small mb-4">
                    Your name, email and profile picture cannot be changed
                    here — you can only update your password.
                  </p>

                  {renderPasswordField(
                    "newPassword",
                    "New Password",
                    showNew,
                    setShowNew,
                    "Enter new password"
                  )}
                  {renderPasswordField(
                    "confirmPassword",
                    "Confirm New Password",
                    showConfirm,
                    setShowConfirm,
                    "Re-enter new password"
                  )}

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      type="submit"
                      className="btn-blue"
                      style={{ borderRadius: "8px" }}
                      disabled={loading}
                    >
                      <FaSave className="me-1" />{" "}
                      {loading ? "Updating..." : "Update Password"}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      style={{ borderRadius: "8px" }}
                      onClick={close}
                      disabled={loading}
                    >
                      <MdCancel className="me-1" /> Cancel
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
      </form>
    </Modal>
  )
}

ProfileModal.propTypes = {
  open: PropTypes.bool,
  close: PropTypes.func,
}

export default ProfileModal
